<template>
  <NavBar pageName="Numerele Mele"></NavBar>

  <v-snackbar
    v-model="snackbarVisible"
    :color="snackbarColor"
    :timeout="snackbarTimeout"
  >
    {{ snackbarMessage }}
    <template v-slot:action>
      <v-btn text color="red" @click="snackbarVisible = false">Inchide</v-btn>
    </template>
  </v-snackbar>

  <v-container style="margin-top: 185px;">
    <h2 class="text-h4 mb-4">Numerele Mele</h2>
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>
          <v-icon start>mdi-phone</v-icon>
          Numerele Mele de Telefon
        </span>
        <v-btn color="primary" @click="openAddDialog">
          <v-icon start>mdi-plus</v-icon>
          Adauga Numar
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Info Alert -->
        <v-alert type="info" variant="tonal" density="compact" class="mb-4" v-if="myMsisdns.length === 0 && !loading">
          Nu ai niciun numar de telefon inregistrat. Apasa butonul "Adauga Numar" pentru a-ti inregistra un numar propriu.
        </v-alert>

        <!-- Loading -->
        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4"></v-progress-linear>

        <!-- MSISDNs Table -->
        <v-data-table
          v-if="myMsisdns.length > 0"
          :headers="headers"
          :items="myMsisdns"
          item-value="id"
          class="elevation-1"
          :items-per-page="20"
          :loading="loading"
        >
          <!-- MSISDN Column -->
          <template v-slot:[`item.msisdn`]="{ item }">
            <span class="font-weight-medium">{{ item.msisdn }}</span>
          </template>

          <!-- MVNO/Network Column -->
          <template v-slot:[`item.mvno_name`]="{ item }">
            <v-chip
              :color="item.own_network ? 'success' : 'grey'"
              size="small"
              variant="tonal"
            >
              <v-icon start size="small">
                {{ item.own_network ? 'mdi-check-circle' : 'mdi-circle-outline' }}
              </v-icon>
              {{ item.mvno_name || 'Necunoscut' }}
            </v-chip>
          </template>

          <!-- Verification Status Column -->
          <template v-slot:[`item.verified`]="{ item }">
            <v-chip
              :color="item.verified_at ? 'success' : 'warning'"
              size="small"
            >
              <v-icon start size="small">
                {{ item.verified_at ? 'mdi-check-decagram' : 'mdi-clock-outline' }}
              </v-icon>
              {{ item.verified_at ? 'Verificat' : 'In asteptare' }}
            </v-chip>
          </template>

          <!-- Verified Date Column -->
          <template v-slot:[`item.verified_at`]="{ item }">
            {{ item.verified_at ? formatDate(item.verified_at) : '-' }}
          </template>

          <!-- Port Requests Column -->
          <template v-slot:[`item.port_requests_count`]="{ item }">
            <v-chip v-if="item.port_requests_count > 0" size="small" color="info">
              {{ item.port_requests_count }}
            </v-chip>
            <span v-else class="text-grey">-</span>
          </template>

          <!-- Actions Column -->
          <template v-slot:[`item.actions`]="{ item }">
            <!-- Active Port Request Chip -->
            <v-chip
              v-if="item.has_active_port_request"
              size="small"
              color="warning"
              variant="tonal"
              class="mr-1"
            >
              <v-icon start size="small">mdi-swap-horizontal</v-icon>
              Portare in curs
            </v-chip>

            <!-- Verify Button (only if not verified and no active port request) -->
            <v-btn
              v-if="!item.verified_at && !item.has_active_port_request"
              size="small"
              color="warning"
              variant="tonal"
              class="mr-1"
              @click="openVerifyDialog(item)"
            >
              <v-icon start size="small">mdi-check</v-icon>
              Verifica
            </v-btn>

            <!-- Request Portability Button (only if verified and no active port request) -->
            <v-btn
              v-if="item.verified_at && !item.has_active_port_request && !item.own_network"
              size="small"
              color="primary"
              variant="tonal"
              class="mr-1"
              @click="requestPortability(item)"
            >
              <v-icon start size="small">mdi-swap-horizontal</v-icon>
              Portare
            </v-btn>

            <!-- View Details in MsisdnsList -->
            <v-btn
              icon
              size="small"
              variant="text"
              color="primary"
              title="Info"
              @click="goToMsisdnsList(item)"
            >
              <v-icon>mdi-eye</v-icon>
            </v-btn>

            <!-- Delete Button (hide if active port request) -->
            <v-btn
              v-if="!item.has_active_port_request"
              icon
              size="small"
              variant="text"
              color="error"
              @click="confirmDelete(item)"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Add MSISDN Dialog -->
    <AddUserMsisdnDialog
      v-if="showAddDialog"
      @close="showAddDialog = false"
      @msisdnAdded="onMsisdnAdded"
    />

    <!-- Verify MSISDN Dialog -->
    <v-dialog v-model="showVerifyDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title>Verifica Numarul</v-card-title>
        <v-card-text>
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            Vei primi un SMS cu un cod de verificare la numarul <strong>{{ selectedMsisdn?.msisdn }}</strong>.
          </v-alert>

          <div v-if="verifyStep === 1">
            <v-btn
              color="primary"
              block
              @click="sendVerificationCode"
              :loading="sendingCode"
            >
              <v-icon start>mdi-send</v-icon>
              Trimite Cod SMS
            </v-btn>
          </div>

          <div v-if="verifyStep === 2">
            <v-otp-input
              v-model="verificationCode"
              length="6"
              type="number"
              :disabled="verifyingCode"
              class="mb-4"
            ></v-otp-input>

            <div class="text-center text-grey mb-3" v-if="codeExpiresIn > 0">
              Codul expira in {{ formatTime(codeExpiresIn) }}
            </div>

            <div class="text-center mb-3">
              <v-btn
                variant="text"
                size="small"
                @click="sendVerificationCode"
                :disabled="resendCooldown > 0 || sendingCode"
              >
                {{ resendCooldown > 0 ? `Retrimite (${resendCooldown}s)` : 'Retrimite codul' }}
              </v-btn>
            </div>
          </div>

          <v-alert v-if="verifyError" type="error" density="compact" class="mt-3">
            {{ verifyError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeVerifyDialog">Anuleaza</v-btn>
          <v-btn
            v-if="verifyStep === 2"
            color="primary"
            @click="verifyCode"
            :loading="verifyingCode"
            :disabled="verificationCode.length !== 6"
          >
            Verifica
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Details Dialog -->
    <v-dialog v-model="showDetailsDialog" max-width="500px">
      <v-card v-if="selectedMsisdn">
        <v-card-title>
          <v-icon start>mdi-phone</v-icon>
          {{ selectedMsisdn.msisdn }}
        </v-card-title>
        <v-card-text>
          <v-list density="compact">
            <v-list-item>
              <template v-slot:prepend>
                <v-icon>mdi-signal</v-icon>
              </template>
              <v-list-item-title>Retea</v-list-item-title>
              <v-list-item-subtitle>
                {{ selectedMsisdn.mvno_name || 'Necunoscut' }}
                <v-chip v-if="selectedMsisdn.own_network" size="x-small" color="success" class="ml-2">
                  Retea proprie
                </v-chip>
              </v-list-item-subtitle>
            </v-list-item>

            <v-list-item>
              <template v-slot:prepend>
                <v-icon>mdi-check-decagram</v-icon>
              </template>
              <v-list-item-title>Status verificare</v-list-item-title>
              <v-list-item-subtitle>
                {{ selectedMsisdn.verified_at ? 'Verificat' : 'Neverificat' }}
              </v-list-item-subtitle>
            </v-list-item>

            <v-list-item v-if="selectedMsisdn.verified_at">
              <template v-slot:prepend>
                <v-icon>mdi-calendar-check</v-icon>
              </template>
              <v-list-item-title>Data verificarii</v-list-item-title>
              <v-list-item-subtitle>
                {{ formatDateTime(selectedMsisdn.verified_at) }}
              </v-list-item-subtitle>
            </v-list-item>

            <v-list-item>
              <template v-slot:prepend>
                <v-icon>mdi-calendar-plus</v-icon>
              </template>
              <v-list-item-title>Data adaugarii</v-list-item-title>
              <v-list-item-subtitle>
                {{ formatDateTime(selectedMsisdn.created_at) }}
              </v-list-item-subtitle>
            </v-list-item>

            <v-list-item v-if="selectedMsisdn.port_requests_count > 0">
              <template v-slot:prepend>
                <v-icon>mdi-swap-horizontal</v-icon>
              </template>
              <v-list-item-title>Cereri portabilitate</v-list-item-title>
              <v-list-item-subtitle>
                {{ selectedMsisdn.port_requests_count }} cereri
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" color="red" @click="showDetailsDialog = false">Inchide</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-error">
          <v-icon start color="error">mdi-alert</v-icon>
          Sterge Numarul
        </v-card-title>
        <v-card-text>
          Esti sigur ca vrei sa stergi numarul <strong>{{ selectedMsisdn?.msisdn }}</strong>?
          <br><br>
          <span class="text-grey">Aceasta actiune nu poate fi anulata.</span>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">Anuleaza</v-btn>
          <v-btn color="error" @click="deleteMsisdn" :loading="deleting">
            Sterge
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Portability Request Dialog -->
    <CreatePortabilityRequestDialog
      v-if="showPortabilityDialog"
      :token="token"
      :msisdn="selectedMsisdn"
      @close="showPortabilityDialog = false"
      @created="onPortRequestCreated"
    />

  </v-container>
</template>

<script>
import NavBar from "@/components/NavBar.vue";
import AddUserMsisdnDialog from "@/components/AddUserMsisdnDialog.vue";
import CreatePortabilityRequestDialog from "@/components/CreatePortabilityRequestDialog.vue";
import useGetLoggedIn from "@/composables/useGetLoggedIn";
import { log } from "@/composables/useLog";
import {
  getUserOwnMsisdns,
  deleteUserMsisdn,
  sendMsisdnVerificationCode,
  verifyMsisdnCode
} from "@/composables/useUserMsisdnsAPI";

export default {
  name: 'MyMsisdnsView',
  components: { NavBar, AddUserMsisdnDialog, CreatePortabilityRequestDialog },
  data() {
    return {
      token: '',
      loading: false,
      myMsisdns: [],

      // Dialogs
      showAddDialog: false,
      showVerifyDialog: false,
      showDetailsDialog: false,
      showDeleteDialog: false,
      showPortabilityDialog: false,

      // Selected MSISDN
      selectedMsisdn: null,

      // Verification state
      verifyStep: 1,
      verificationCode: '',
      sendingCode: false,
      verifyingCode: false,
      verifyError: null,
      codeExpiresIn: 0,
      resendCooldown: 0,
      expiryTimer: null,
      cooldownTimer: null,

      // Delete state
      deleting: false,

      // Snackbar
      snackbarVisible: false,
      snackbarMessage: "",
      snackbarColor: "success",
      snackbarTimeout: 3000,

      // Table headers
      headers: [
        { title: "Numar", key: "msisdn", width: "150px" },
        { title: "Retea", key: "mvno_name", width: "150px" },
        { title: "Status", key: "verified", width: "130px" },
        { title: "Data Verificare", key: "verified_at", width: "140px" },
        { title: "Cereri Port", key: "port_requests_count", width: "100px" },
        { title: "Actiuni", key: "actions", sortable: false, width: "250px" }
      ]
    };
  },
  methods: {
    showSnackbar(message, color = "success") {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbarVisible = true;
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('ro-RO');
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString('ro-RO');
    },

    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    },

    async loadMsisdns() {
      this.loading = true;
      try {
        const response = await getUserOwnMsisdns(this.token);
        if (!response.error) {
          this.myMsisdns = response.msisdns || [];
          log("Loaded " + this.myMsisdns.length + " MSISDNs");
        } else {
          this.showSnackbar(response.message || "Eroare la incarcarea numerelor", "error");
        }
      } catch (err) {
        log("Error loading MSISDNs:", err);
        this.showSnackbar("Eroare la incarcarea numerelor", "error");
      } finally {
        this.loading = false;
      }
    },

    openAddDialog() {
      this.showAddDialog = true;
    },

    onMsisdnAdded() {
      this.showAddDialog = false;
      this.showSnackbar("Numarul a fost adaugat cu succes!");
      this.loadMsisdns();
    },

    openVerifyDialog(msisdn) {
      this.selectedMsisdn = msisdn;
      this.verifyStep = 1;
      this.verificationCode = '';
      this.verifyError = null;
      this.codeExpiresIn = 0;
      this.resendCooldown = 0;
      this.showVerifyDialog = true;
    },

    closeVerifyDialog() {
      this.stopTimers();
      this.showVerifyDialog = false;
      this.selectedMsisdn = null;
    },

    async sendVerificationCode() {
      this.sendingCode = true;
      this.verifyError = null;

      try {
        const response = await sendMsisdnVerificationCode(this.token, this.selectedMsisdn.id, 'sms');

        if (response.error) {
          this.verifyError = response.message || "Eroare la trimiterea codului";
        } else {
          this.verifyStep = 2;
          this.codeExpiresIn = response.expires_in || 600;
          this.resendCooldown = response.cooldown || 60;
          this.verificationCode = '';

          this.startExpiryTimer();
          this.startCooldownTimer();
        }
      } catch (err) {
        log("Error sending verification code:", err);
        this.verifyError = "Eroare la trimiterea codului";
      } finally {
        this.sendingCode = false;
      }
    },

    async verifyCode() {
      if (this.verificationCode.length !== 6) return;

      this.verifyingCode = true;
      this.verifyError = null;

      try {
        const response = await verifyMsisdnCode(this.token, this.selectedMsisdn.id, this.verificationCode);

        if (response.error) {
          this.verifyError = response.message || "Cod invalid";
          this.verificationCode = '';
        } else {
          this.stopTimers();
          this.showVerifyDialog = false;
          this.showSnackbar("Numarul a fost verificat cu succes!");
          this.loadMsisdns();
        }
      } catch (err) {
        log("Error verifying code:", err);
        this.verifyError = "Eroare la verificare";
      } finally {
        this.verifyingCode = false;
      }
    },

    startExpiryTimer() {
      this.stopExpiryTimer();
      this.expiryTimer = setInterval(() => {
        if (this.codeExpiresIn > 0) {
          this.codeExpiresIn--;
        } else {
          this.stopExpiryTimer();
          this.verifyError = "Codul a expirat. Te rugam sa retrimiti un cod nou.";
        }
      }, 1000);
    },

    startCooldownTimer() {
      this.stopCooldownTimer();
      this.cooldownTimer = setInterval(() => {
        if (this.resendCooldown > 0) {
          this.resendCooldown--;
        } else {
          this.stopCooldownTimer();
        }
      }, 1000);
    },

    stopExpiryTimer() {
      if (this.expiryTimer) {
        clearInterval(this.expiryTimer);
        this.expiryTimer = null;
      }
    },

    stopCooldownTimer() {
      if (this.cooldownTimer) {
        clearInterval(this.cooldownTimer);
        this.cooldownTimer = null;
      }
    },

    stopTimers() {
      this.stopExpiryTimer();
      this.stopCooldownTimer();
    },

    goToMsisdnsList(item) {
      this.$router.push({ name: 'msisdns', query: { search: item.msisdn } });
    },

    viewDetails(msisdn) {
      this.selectedMsisdn = msisdn;
      this.showDetailsDialog = true;
    },

    confirmDelete(msisdn) {
      this.selectedMsisdn = msisdn;
      this.showDeleteDialog = true;
    },

    async deleteMsisdn() {
      if (!this.selectedMsisdn) return;

      this.deleting = true;
      try {
        const response = await deleteUserMsisdn(this.token, this.selectedMsisdn.id);

        if (!response.error) {
          this.showSnackbar("Numarul a fost sters");
          this.showDeleteDialog = false;
          this.loadMsisdns();
        } else {
          this.showSnackbar(response.message || "Eroare la stergere", "error");
        }
      } catch (err) {
        log("Error deleting MSISDN:", err);
        this.showSnackbar("Eroare la stergere", "error");
      } finally {
        this.deleting = false;
      }
    },

    requestPortability(msisdn) {
      this.selectedMsisdn = msisdn;
      this.showPortabilityDialog = true;
    },

    onPortRequestCreated() {
      this.showPortabilityDialog = false;
      this.showSnackbar("Cererea de portabilitate a fost trimisa cu succes!");
      this.loadMsisdns();
    }
  },
  created() {
    const { getLoggedIn, token } = useGetLoggedIn();
    getLoggedIn().then(() => {
      log("MyMsisdnsView created");
      this.token = token.value;
      this.loadMsisdns();
    });
  },
  beforeUnmount() {
    this.stopTimers();
  }
};
</script>
